<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  campaignId: { type: [String, Number], required: true },
  actionType: { type: String, default: 'new_campaign' },
  contactIds: { type: Array, default: () => [] },
});

const { t } = useI18n();
const router = useRouter();

const dialogRef = ref(null);
const isProcessing = ref(false);
const webhookUrl = ref('');
const selectedLabels = ref([]);

const labels = useMapGetter('labels/getLabels');
const labelOptions = computed(
  () => labels.value?.map(l => ({ value: l.title, label: l.title })) ?? []
);

const isAddTag = computed(() => props.actionType === 'add_tag');
const isWebhook = computed(() => props.actionType === 'webhook');
const isNewCampaign = computed(() => props.actionType === 'new_campaign');

const title = computed(() => {
  if (isNewCampaign.value) return t('CAMPAIGN.WHATSAPP.RETARGET.NEW_CAMPAIGN');
  if (isAddTag.value) return t('CAMPAIGN.WHATSAPP.RETARGET.ADD_TAG');
  return t('CAMPAIGN.WHATSAPP.RETARGET.SEND_WEBHOOK');
});

const canSubmit = computed(() => {
  if (isAddTag.value) return selectedLabels.value.length > 0;
  if (isWebhook.value) return webhookUrl.value.startsWith('http');
  return true;
});

const handleConfirm = async () => {
  isProcessing.value = true;
  try {
    const payload = {
      action_type: props.actionType,
      contact_ids: props.contactIds,
    };
    if (isAddTag.value) payload.labels = selectedLabels.value;
    if (isWebhook.value) payload.webhook_url = webhookUrl.value;

    const { data } = await CampaignsAPI.retarget(props.campaignId, payload);

    if (isNewCampaign.value && data.campaign_id) {
      useAlert(
        t('CAMPAIGN.WHATSAPP.RETARGET.SUCCESS_CAMPAIGN', {
          count: data.contacts_count,
        })
      );
      router.push({
        name: 'campaigns_whatsapp_index',
      });
    } else {
      useAlert(
        t('CAMPAIGN.WHATSAPP.RETARGET.SUCCESS_GENERIC', {
          count: data.contacts_count,
        })
      );
    }

    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.WHATSAPP.RETARGET.ERROR')
    );
  } finally {
    isProcessing.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog ref="dialogRef" :title="title" width="lg" @confirm="handleConfirm">
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{
          t('CAMPAIGN.WHATSAPP.RETARGET.DESCRIPTION', {
            count: contactIds.length,
          })
        }}
      </p>

      <!-- New Campaign: no extra fields needed -->
      <div
        v-if="isNewCampaign"
        class="p-3 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.RETARGET.NEW_CAMPAIGN_INFO') }}
      </div>

      <!-- Add Tag -->
      <div v-if="isAddTag" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.RETARGET.SELECT_LABELS') }}
        </label>
        <TagMultiSelectComboBox
          v-model="selectedLabels"
          :options="labelOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.RETARGET.SELECT_LABELS')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <!-- Webhook -->
      <div v-if="isWebhook">
        <Input
          v-model="webhookUrl"
          :label="t('CAMPAIGN.WHATSAPP.RETARGET.WEBHOOK_URL')"
          placeholder="https://n8n.example.com/webhook/..."
          type="url"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3 w-full">
        <button
          type="button"
          class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
          @click="dialogRef?.close()"
        >
          {{ t('CAMPAIGN.WHATSAPP.RETARGET.CANCEL') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium rounded-lg text-white bg-[#322F23] hover:bg-[#322F23]/90 disabled:opacity-50 cursor-pointer"
          :disabled="!canSubmit || isProcessing"
          @click="handleConfirm"
        >
          {{ t('CAMPAIGN.WHATSAPP.RETARGET.CONFIRM') }}
        </button>
      </div>
    </template>
  </Dialog>
</template>
